<script>
export default {
  name: 'AppButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'success', 'danger'].includes(value),
    },
    outline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    type: {
      type: String,
      default: 'button',
    },
  },
  computed: {
    buttonClasses() {
      return [
        'app-button',
        `app-button--${this.variant}`,
        `app-button--${this.size}`,
        { 'app-button--outline': this.outline },
        { 'app-button--disabled': this.disabled },
      ];
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled) {
        this.$emit('click', event);
      }
    }
  }
};
</script>

<template>
    <button :type="type" :class="buttonClasses" :disabled="disabled" @click="handleClick">
        <span class="app-button__text">
            <slot></slot>
        </span>
    </button>
</template>

<style scoped lang="scss">
.app-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 4px;
    font-weight: 400;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    outline: none;
    box-sizing: border-box;
    max-width: 100%;

    /* Truncation / nowrap behavior */
    .app-button__text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
        max-width: 100%;
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px rgba(68, 117, 180, 0.4);
    }

    &--disabled,
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
    }

    /* Primary Variant */
    &--primary {
        background-color: #4475b4;
        border: 1px solid #4475b4;
        color: #ffffff;

        &:hover:not(:disabled) {
            background-color: #365d90;
            border: 1px solid #365d90;
        }

        &:active:not(:disabled) {
            background-color: #2b4a73;
            border: 1px solid #2b4a73;
        }

        &.app-button--outline {
            background-color: transparent;
            color: #4475b4;
            border: 1px solid #4475b4;

            &:hover:not(:disabled) {
                background-color: rgba(68, 117, 180, 0.1);
            }

            &:active:not(:disabled) {
                background-color: rgba(68, 117, 180, 0.2);
            }
        }
    }

    /* Secondary Variant */
    &--secondary {
        background-color: #e3e3e4;
        border: 1px solid #e3e3e4;
        color: #333333;

        &:hover:not(:disabled) {
            background-color: #cccccc;
            border: 1px solid #cccccc;
        }

        &:active:not(:disabled) {
            background-color: #b3b3b3;
            border: 1px solid #b3b3b3;
        }

        &.app-button--outline {
            background-color: transparent;
            color: #333333;
            border: 1px solid #e3e3e4;

            &:hover:not(:disabled) {
                background-color: rgba(227, 227, 228, 0.3);
            }

            &:active:not(:disabled) {
                background-color: rgba(227, 227, 228, 0.5);
            }
        }
    }

    /* Success Variant */
    &--success {
        background-color: #9cc05a;
        border: 1px solid #9cc05a;
        color: #ffffff;

        &:hover:not(:disabled) {
            background-color: #8ab748;
            border: 1px solid #8ab748;
        }

        &:active:not(:disabled) {
            background-color: #78a23b;
            border: 1px solid #78a23b;
        }

        &.app-button--outline {
            background-color: transparent;
            color: #9cc05a;
            border: 1px solid #9cc05a;

            &:hover:not(:disabled) {
                background-color: rgba(156, 192, 90, 0.1);
            }

            &:active:not(:disabled) {
                background-color: rgba(156, 192, 90, 0.2);
            }
        }
    }

    /* Danger Variant */
    &--danger {
        background-color: #dc3545;
        border: 1px solid #dc3545;
        color: #ffffff;

        &:hover:not(:disabled) {
            //background-color: #c82333;
            //border: 1px solid #c82333;
			background-color: transparent;
			border:none;
        }

        &:active:not(:disabled) {
            background-color: #bd2130;
            border: 1px solid #bd2130;
        }

        &.app-button--outline {
            background-color: transparent;
            color: #dc3545;
            border: 1px solid #dc3545;

            &:hover:not(:disabled) {
                background-color: rgba(220, 53, 69, 0.1);
            }

            &:active:not(:disabled) {
                background-color: rgba(220, 53, 69, 0.2);
            }
        }
    }

    /* Sizes */
    &--sm {
        padding: 6px 8px;
        font-size: 12px;
    }

    &--md {
        padding: 8px 16px;
        font-size: 14px;
    }

    &--lg {
        padding: 12px 24px;
        font-size: 16px;
    }
}
</style>
